(() => {
  if (window.__buddySellingPlansIntegrationLoaded) {
    if (window.BuddySubscriptionBox?.init) {
      window.BuddySubscriptionBox.init(document);
    }
    return;
  }

  window.__buddySellingPlansIntegrationLoaded = true;
  window.__buddyPurchaseIntents = window.__buddyPurchaseIntents || {};

const hiddenClass = 'pi-selling-plan-hidden';

class SellingPlansWidget {
  constructor(sellingPlansWidgetContainer) {
    this.enablePerformanceObserver();
    this.sellingPlansWidgetContainer = sellingPlansWidgetContainer;
    this.purchaseIntent = window.__buddyPurchaseIntents[this.purchaseIntentKey] || null;
    this.appendSellingPlanInputs();
    this.updateSellingPlanInputsValues();
    this.listenToVariantChange();
    this.listenToSellingPlanFormRadioButtonChange();
    this.listenToPurchaseCardClick();
    this.updatePrice();
    this.initializeWidget();
  }

  initializeWidget() {
    // Ensure the widget is properly initialized
    if (!this.shopifySection) {
      console.warn('SellingPlansWidget: Could not find shopify section');
      return;
    }
    
    // Set initial state
    this.handleSellingPlanFormVisibility();
    this.updateSellingPlanInputsValues();
    
    // Set initial active item on load
    this.setInitialActiveItem();
  }

  /*
    Set the initial active item on page load
  */
  setInitialActiveItem() {
    // Find all initially checked radio buttons across all variants
    const checkedRadios = this.shopifySection.querySelectorAll('input[type="radio"]:checked');
    
    checkedRadios.forEach(checkedRadio => {
      // Add data-active-item to the parent of each checked radio
      const parentItem = checkedRadio.closest('.pi-selling-plan-options__item');
      if (parentItem) {
        parentItem.setAttribute('data-active-item', 'true');
      }
    });
  }

  get sectionId() {
    return this.sellingPlansWidgetContainer.getAttribute('data-section-id');
  }

  get purchaseIntentKey() {
    const productId = this.sellingPlansWidgetContainer.getAttribute('data-product-id') || 'product';
    return `${this.sectionId}:${productId}`;
  }

  get shopifySection() {
    return document.querySelector(`#shopify-section-${this.sectionId}`);
  }

  /*
    We are careful to target the correct form, as there are instances when we encounter an installment form that we specifically aim to avoid interacting with.
  */
  get variantIdInput() {
    const forms = this.addToCartForms;
    if (!forms || forms.length === 0) return null;
    
    return (
      forms[1]?.querySelector(`input[name="id"]`) ||
      forms[1]?.querySelector(`select[name="id"]`) ||
      forms[0].querySelector(`input[name="id"]`) ||
      forms[0].querySelector(`select[name="id"]`)
    );
  }

  get priceElement() {
    return this.shopifySection.querySelector('.price');
  }

  get comparedAtPrice() {
    return this.shopifySection.querySelector('.price__sale');
  }

  get currentVariantSection() {
    if (!this.variantIdInput) return null;
    return this.shopifySection.querySelector(
      `section[data-variant-id="${this.variantIdInput.value}"]`,
    );
  }

  get visibleSellingPlanForm() {
    return this.currentVariantSection;
  }

  get visibleSellingPlanForms() {
    if (!this.currentVariantSection) return [];
    return Array.from(this.shopifySection.querySelectorAll(
      `.pi-selling-plan-section[data-option1="${CSS.escape(this.currentVariantSection.dataset.option1 || '')}"]`,
    ));
  }

  get isVariantAvailable() {
    return this.selectedPurchaseOption?.getAttributeNames().includes('disabled') || false;
  }

  get sellingPlanInput() {
    return this.shopifySection.querySelector('.pi-selected-selling-plan-id');
  }

  get addToCartForms() {
    return this.shopifySection.querySelectorAll('[action*="/cart/add"]');
  }

  /*
    To enable the addition of a selling plan to a cart, it's necessary to include an input with the name "selling_plan", which will carry the selling ID as its value. When a buyer clicks on 'add to cart', the appropriate selling plan ID is added to their cart.
  */
  appendSellingPlanInputs() {
    if (!this.sellingPlanInput) return;
    
    this.addToCartForms.forEach((addToCartForm) => {
      const existingInput = addToCartForm.querySelector('.pi-selected-selling-plan-id');
      if (!existingInput) {
        addToCartForm.appendChild(this.sellingPlanInput.cloneNode());
      }
    });
  }

  showSellingPlanForm(sellingPlanFormForSelectedVariant) {
    if (sellingPlanFormForSelectedVariant) {
      sellingPlanFormForSelectedVariant.classList.remove(hiddenClass);
    }
  }

  hideSellingPlanForms(sellingPlanFormsForUnselectedVariants) {
    sellingPlanFormsForUnselectedVariants.forEach((element) => {
      element.classList.add(hiddenClass);
    });
  }

  /*
    Each product variant comes with a selling plan selection box that the buyer can interact with.
    When a buyer chooses a different variant, we ensure that only the relevant selling plan selection box is displayed.
    This guarantees that only the selling plan associated with the selected variant is shown.
  */
  handleSellingPlanFormVisibility(purchaseIntent = this.purchaseIntent) {
    if (!this.variantIdInput) return;
    
    const currentSection = this.currentVariantSection;
    const currentOption1 = currentSection?.dataset.option1 || '';
    const sellingPlanFormsForSelectedSize = currentOption1
      ? this.shopifySection.querySelectorAll(`.pi-selling-plan-section[data-option1="${CSS.escape(currentOption1)}"]`)
      : [];
    const sellingPlanFormsForUnselectedSizes = currentOption1
      ? this.shopifySection.querySelectorAll(`.pi-selling-plan-section:not([data-option1="${CSS.escape(currentOption1)}"])`)
      : this.shopifySection.querySelectorAll('.pi-selling-plan-section');

    sellingPlanFormsForSelectedSize.forEach((form) => this.showSellingPlanForm(form));
    this.hideSellingPlanForms(sellingPlanFormsForUnselectedSizes);
    this.ensureVisiblePurchaseOption(currentSection, purchaseIntent);
  }

  handleVariantChange() {
    const purchaseIntent = this.purchaseIntent || this.getPurchaseIntent(this.selectedPurchaseOption);
    this.handleSellingPlanFormVisibility(purchaseIntent);
    this.updateSellingPlanInputsValues();
    this.listenToSellingPlanFormRadioButtonChange();
    this.updatePrice();
  }

  /*
    The functions listenToVariantChange() and listenToAddToCartForms() are implemented to track when a product variant is altered or when the product form is updated.
    The identification of the variant is crucial as it dictates which selling plan box should be displayed.
  */
  listenToVariantChange() {
    this.listenToAddToCartForms();
    
    if (this.variantIdInput && this.variantIdInput.tagName === 'INPUT') {
      const variantIdObserver = new MutationObserver((mutationList) => {
        mutationList.forEach((mutationRecord) => {
          if (mutationRecord.type === 'attributes' && mutationRecord.attributeName === 'value') {
            this.handleVariantChange();
          }
        });
      });

      variantIdObserver.observe(this.variantIdInput, {
        attributes: true,
        attributeFilter: ['value']
      });
    }
  }

  listenToAddToCartForms() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.addEventListener('change', () => {
        this.handleVariantChange();
      });
    });
  }

  get regularPriceElement() {
    return this.shopifySection.querySelector('.price__regular');
  }

  get salePriceElement() {
    return this.shopifySection.querySelector('.price__sale');
  }

  get salePriceValue() {
    return this.salePriceElement?.querySelector('.price-item--sale');
  }

  get regularPriceValue() {
    return this.salePriceElement?.querySelector('.price-item--regular');
  }

  get sellingPlanAllocationPrice() {
    if (!this.selectedPurchaseOption) return null;
    return document.getElementById(
      `${this.selectedPurchaseOption.dataset.sellingPlanGroupId}_allocation_price`,
    );
  }

  get selectedPurchaseOptionPrice() {
    return this.selectedPurchaseOption?.dataset.variantPrice || '';
  }

  get selectedPurchaseOptionComparedAtPrice() {
    return this.selectedPurchaseOption?.dataset.variantCompareAtPrice || '';
  }

  get price() {
    return this.sellingPlanAllocationPrices?.price ?? null;
  }

  /*
    We aim to ascertain whether a compared price exists, which would indicate that the currently selected input has a discount applied to it.
    If a discount is detected, the discounted price is displayed; otherwise, the regular price is shown.
  */
  updatePrice() {
    if (!this.priceElement) return;
    
    if (
      !this.selectedPurchaseOptionComparedAtPrice ||
      this.selectedPurchaseOptionComparedAtPrice ===
        this.selectedPurchaseOptionPrice
    ) {
      this.showRegularPrice();
      this.hideSalePrice();
      this.priceElement.classList.remove('price--on-sale');
    } else {
      this.showSalePrice();
      this.hideRegularPrice();
      this.priceElement.classList.add('price--on-sale');
    }
  }

  hideSalePrice() {
    if (this.salePriceElement) {
      this.salePriceElement.style.display = 'none';
    }
  }

  hideRegularPrice() {
    if (this.regularPriceElement) {
      this.regularPriceElement.style.display = 'none';
    }
  }

  showRegularPrice() {
    if (this.regularPriceElement) {
      this.regularPriceElement.style.display = 'block';
    }
    if (this.shopifySection.querySelector('.price__sale')) {
      this.shopifySection.querySelector('.price__sale').style.display = 'none';
    }
  }

  showSalePrice() {
    if (this.salePriceElement) {
      this.salePriceElement.style.display = 'block';
    }
    if (this.regularPriceValue) {
      this.regularPriceValue.innerHTML = this.selectedPurchaseOptionComparedAtPrice;
    }
    if (this.salePriceValue) {
      this.salePriceValue.innerHTML = this.selectedPurchaseOptionPrice;
    }
  }

  get sellingPlanInputs() {
    return this.shopifySection.querySelectorAll('.pi-selected-selling-plan-id');
  }

  updateSellingPlanInputsValues() {
    this.sellingPlanInputs.forEach((sellingPlanInput) => {
      sellingPlanInput.value = this.sellingPlanInputValue;
    });
  }

  get sellingPlanInputValue() {
    return this.selectedPurchaseOption?.dataset.sellingPlanId ?? '';
  }

  get selectedPurchaseOption() {
    return this.sellingPlansWidgetContainer.querySelector(
      '.pi-selling-plan-section:not(.pi-selling-plan-hidden) input[type="radio"]:checked',
    );
  }

  set selectedPurchaseOption(selectedPurchaseOption) {
    this._selectedPurchaseOption = selectedPurchaseOption;
  }


  getPurchaseIntent(purchaseOption) {
    if (!purchaseOption) return null;
    if (purchaseOption.dataset.radioType === 'one_time_purchase') {
      return { type: 'one_time' };
    }

    const billingDays = Number(purchaseOption.dataset.promotionBillingDays) || 0;
    return {
      type: 'subscription',
      duration: billingDays >= 150 ? 'long' : 'short',
      billingDays,
    };
  }

  findPurchaseOptionForIntent(purchaseIntent) {
    if (!purchaseIntent) return null;
    const availableOptions = Array.from(this.sellingPlansWidgetContainer.querySelectorAll(
      '.pi-selling-plan-section:not(.pi-selling-plan-hidden) input[type="radio"]:not(:disabled)',
    ));

    if (purchaseIntent.type === 'one_time') {
      return availableOptions.find((option) => option.dataset.radioType === 'one_time_purchase') || null;
    }

    const subscriptionOptions = availableOptions.filter((option) => option.dataset.radioType === 'selling_plan');
    const matchingDuration = subscriptionOptions.filter((option) => {
      const billingDays = Number(option.dataset.promotionBillingDays) || 0;
      return purchaseIntent.duration === 'long' ? billingDays >= 150 : billingDays < 150;
    });
    const candidates = matchingDuration.length > 0 ? matchingDuration : subscriptionOptions;

    return candidates.sort((first, second) => {
      const firstDays = Number(first.dataset.promotionBillingDays) || Number.POSITIVE_INFINITY;
      const secondDays = Number(second.dataset.promotionBillingDays) || Number.POSITIVE_INFINITY;
      return Math.abs(firstDays - purchaseIntent.billingDays) - Math.abs(secondDays - purchaseIntent.billingDays);
    })[0] || null;
  }

  ensureVisiblePurchaseOption(currentSection = this.currentVariantSection, purchaseIntent = this.purchaseIntent) {
    const checkedVisible = this.selectedPurchaseOption;
    const intendedRadio = this.findPurchaseOptionForIntent(purchaseIntent);
    const fallbackRadio = intendedRadio || checkedVisible || currentSection?.querySelector('input[type="radio"]:not(:disabled)') ||
      this.sellingPlansWidgetContainer.querySelector('.pi-selling-plan-section:not(.pi-selling-plan-hidden) input[type="radio"]:not(:disabled)');

    if (fallbackRadio) {
      fallbackRadio.checked = true;
      this.handleRadioButtonChange(fallbackRadio, {
        skipVariantSync: fallbackRadio.dataset.variantId === this.variantIdInput?.value,
      });
    }
  }

  syncVariantFromPurchaseOption(selectedPurchaseOption) {
    if (!selectedPurchaseOption?.dataset.variantId || selectedPurchaseOption.dataset.variantId === this.variantIdInput?.value) return false;

    let variantChanged = false;

    ['1', '2', '3'].forEach((optionPosition) => {
      const optionValue = selectedPurchaseOption.dataset[`variantOption${optionPosition}`];
      if (!optionValue) return;

      const optionInput = this.shopifySection.querySelector(
        `.variant-input-wrapper[data-option-index="option${optionPosition}"] input[data-option-value="${CSS.escape(optionValue)}"]`,
      );

      if (optionInput && !optionInput.checked) {
        variantChanged = true;
        optionInput.checked = true;
        optionInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    return variantChanged;
  }

  restorePurchaseOptionFocusAfterVariantRender(selectedPurchaseOption) {
    const productId = this.sellingPlansWidgetContainer.dataset.productId || '';
    const radioType = selectedPurchaseOption.dataset.radioType || '';
    const variantId = selectedPurchaseOption.dataset.variantId || '';
    const sellingPlanId = selectedPurchaseOption.dataset.sellingPlanId || '';
    const selectorParts = [
      'input[type="radio"]',
      `[data-radio-type="${CSS.escape(radioType)}"]`,
      `[data-variant-id="${CSS.escape(variantId)}"]`,
    ];

    if (sellingPlanId) {
      selectorParts.push(`[data-selling-plan-id="${CSS.escape(sellingPlanId)}"]`);
    }

    document.addEventListener('productInfo:loaded', () => {
      initializeSellingPlanFeatures(document);

      const replacementWidget = this.shopifySection?.querySelector(
        `.pi-selling-plan-app-container[data-product-id="${CSS.escape(productId)}"]`,
      );
      const replacementRadio = replacementWidget?.querySelector(selectorParts.join(''));
      const activeElement = document.activeElement;

      if (!replacementRadio || replacementRadio.disabled) return;
      if (activeElement && activeElement !== document.body && activeElement !== selectedPurchaseOption) return;

      requestAnimationFrame(() => {
        const currentActiveElement = document.activeElement;
        if (currentActiveElement && currentActiveElement !== document.body && currentActiveElement !== selectedPurchaseOption) return;
        replacementRadio.focus({ preventScroll: true });
      });
    }, { once: true });
  }

  handleRadioButtonChange(selectedPurchaseOption, options = {}) {
    const shouldRestoreFocus = document.activeElement === selectedPurchaseOption;
    this.selectedPurchaseOption = selectedPurchaseOption;
    this.purchaseIntent = this.getPurchaseIntent(selectedPurchaseOption);
    window.__buddyPurchaseIntents[this.purchaseIntentKey] = this.purchaseIntent;
    let variantChanged = false;
    if (!options.skipVariantSync) {
      variantChanged = this.syncVariantFromPurchaseOption(selectedPurchaseOption);
    }
    if (shouldRestoreFocus && variantChanged) {
      this.restorePurchaseOptionFocusAfterVariantRender(selectedPurchaseOption);
    }
    this.updateSellingPlanInputsValues();
    this.updatePrice();
    
    // Add data-active-item to the parent .pi-selling-plan-options__item
    if (selectedPurchaseOption) {
      this.sellingPlansWidgetContainer.querySelectorAll('.pi-selling-plan-options__item').forEach(item => {
        item.removeAttribute('data-active-item');
      });

      const parentItem = selectedPurchaseOption.closest('.pi-selling-plan-options__item');
      if (parentItem) {
        parentItem.setAttribute('data-active-item', 'true');
      }
    }

    this.sellingPlansWidgetContainer.dispatchEvent(new CustomEvent('buddy:purchase-option-change', {
      bubbles: true,
      detail: {
        selectedOption: selectedPurchaseOption || null,
      },
    }));
  }


  listenToPurchaseCardClick() {
    this.sellingPlansWidgetContainer.querySelectorAll('.pi-selling-plan-options__item').forEach((card) => {
      if (card.dataset.piCardClickInitialized === 'true') return;

      card.addEventListener('click', (event) => {
        if (event.target.closest('a, button, select, input, label')) return;

        const radio = card.querySelector('input[type="radio"]');
        if (!radio || radio.disabled || radio.checked) return;

        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });

      card.dataset.piCardClickInitialized = 'true';
    });
  }

  handlePurchaseOptionKeydown(event) {
    const supportedKeys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (!supportedKeys.includes(event.key)) return;

    const radios = Array.from(this.sellingPlansWidgetContainer.querySelectorAll(
      '.pi-selling-plan-section:not(.pi-selling-plan-hidden) input[type="radio"]:not(:disabled)',
    ));
    const currentIndex = radios.indexOf(event.currentTarget);
    if (currentIndex < 0 || radios.length === 0) return;

    event.preventDefault();

    let nextIndex;
    if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = radios.length - 1;
    } else {
      const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
      nextIndex = (currentIndex + direction + radios.length) % radios.length;
    }

    const nextRadio = radios[nextIndex];
    nextRadio.focus({ preventScroll: true });
    nextRadio.checked = true;
    nextRadio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  listenToSellingPlanFormRadioButtonChange() {
    const radios = this.sellingPlansWidgetContainer.querySelectorAll('.pi-selling-plan-section:not(.pi-selling-plan-hidden) input[type="radio"]');
    if (!radios) return;
    
    radios.forEach((radio) => {
      if (radio.dataset.piSellingPlanChangeInitialized === 'true') return;

      radio.addEventListener('change', (event) => {
        this.handleRadioButtonChange(event.target);
      });
      radio.addEventListener('keydown', (event) => {
        this.handlePurchaseOptionKeydown(event);
      });
      radio.dataset.piSellingPlanChangeInitialized = 'true';
    });
  }

  enablePerformanceObserver() {
    if (!window.PerformanceObserver) return;
    
    const performanceObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.initiatorType !== 'fetch') return;

        const url = new URL(entry.name);
        /*
          When a buyer selects a product variant, a fetch request is initiated.
          Upon completion of this fetch request, we update the price to reflect the correct value.
        */
        if (url.search.includes('variant') || url.search.includes('variants')) {
          setTimeout(() => {
            this.updatePrice();
          }, 100);
        }
      });
    });

    try {
      performanceObserver.observe({entryTypes: ['resource']});
    } catch (error) {
      console.warn('PerformanceObserver not supported:', error);
    }
  }
}

function initializeSellingPlansWidgets(root = document) {
  root
    .querySelectorAll('.pi-selling-plan-app-container')
    .forEach((sellingPlansWidgetContainer) => {
      if (sellingPlansWidgetContainer.dataset.piSellingPlansInitialized === 'true') return;

      new SellingPlansWidget(sellingPlansWidgetContainer);
      sellingPlansWidgetContainer.dataset.piSellingPlansInitialized = 'true';
    });
}

function initializeSellingPlanDropdowns(root = document) {
  root.querySelectorAll('.pi-selling-plan-options__item__dropdown-select').forEach(select => {
    if (select.dataset.piDropdownInitialized === 'true') return;

    select.addEventListener('change', function () {
      const selectedValue = this.value;

      // ✅ Check the correct input
      document.querySelectorAll('.variant-input-wrapper + .variant-input-wrapper input').forEach(input => {
        input.checked = (input.value === selectedValue);
        if (input.checked) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      // ✅ Reset all dropdowns back to their original `selected` defaults
      document.querySelectorAll('.pi-selling-plan-options__item__dropdown-select').forEach(otherSelect => {
        otherSelect.querySelectorAll('option').forEach(option => {
          // Reset to original Shopify default
          if (option.hasAttribute('selected')) {
            option.selected = true;
          } else {
            option.selected = false;
          }
        });
      });
    });

    select.dataset.piDropdownInitialized = 'true';
  });
}

function initializeSellingPlanFeatures(root = document) {
  initializeSellingPlansWidgets(root);
  initializeSellingPlanDropdowns(root);
}

// Initialize widgets when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeSellingPlanFeatures(document);
  });
} else {
  initializeSellingPlanFeatures(document);
}

// Re-initialize when Shopify's theme editor re-renders a section.
document.addEventListener('shopify:section:load', (event) => {
  initializeSellingPlanFeatures(event.target);
});

// Variant renders replace custom Liquid blocks with fresh markup.
document.addEventListener('productInfo:loaded', () => {
  initializeSellingPlanFeatures(document);
});

  window.BuddySubscriptionBox = {
    init: initializeSellingPlanFeatures,
    SellingPlansWidget,
  };
})();
